/**
 * Filter operator definitions for the report builder.
 *
 * Maps field types to available comparison operators and provides
 * suffix conventions for encoding operator + value in the filter state.
 */

/** Operator identifiers used in filter state keys */
export type FilterOperator =
  | 'eq'
  | 'ne'
  | 'in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'contains'
  | 'starts_with'
  | 'not_in'
  | 'is_empty'
  | 'is_not_empty'
  | 'relative_date';

export interface OperatorOption {
  key: FilterOperator;
  label: string;
  /** Longer description shown in dropdown menu */
  description?: string;
  requiresValue: boolean;
  /** Whether this operator requires two value inputs (range) */
  requiresRange: boolean;
}

/** All available operators with their display labels */
const ALL_OPERATORS: Record<FilterOperator, OperatorOption> = {
  eq: { key: 'eq', label: 'equals', requiresValue: true, requiresRange: false },
  ne: {
    key: 'ne',
    label: 'not equal',
    description: 'does not equal the value',
    requiresValue: true,
    requiresRange: false,
  },
  in: {
    key: 'in',
    label: 'one of',
    description: 'matches any of selected values',
    requiresValue: true,
    requiresRange: false,
  },
  gt: {
    key: 'gt',
    label: 'greater than',
    description: 'is greater than the value',
    requiresValue: true,
    requiresRange: false,
  },
  gte: {
    key: 'gte',
    label: 'at least',
    description: 'is greater than or equal to',
    requiresValue: true,
    requiresRange: false,
  },
  lt: {
    key: 'lt',
    label: 'less than',
    description: 'is less than the value',
    requiresValue: true,
    requiresRange: false,
  },
  lte: {
    key: 'lte',
    label: 'at most',
    description: 'is less than or equal to',
    requiresValue: true,
    requiresRange: false,
  },
  between: {
    key: 'between',
    label: 'between',
    description: 'is between two values',
    requiresValue: false,
    requiresRange: true,
  },
  contains: { key: 'contains', label: 'contains', requiresValue: true, requiresRange: false },
  starts_with: {
    key: 'starts_with',
    label: 'starts with',
    requiresValue: true,
    requiresRange: false,
  },
  not_in: {
    key: 'not_in',
    label: 'not one of',
    description: 'is not one of selected values',
    requiresValue: true,
    requiresRange: false,
  },
  is_empty: {
    key: 'is_empty',
    label: 'is empty',
    description: 'field has no value',
    requiresValue: false,
    requiresRange: false,
  },
  is_not_empty: {
    key: 'is_not_empty',
    label: 'is not empty',
    description: 'field has a value',
    requiresValue: false,
    requiresRange: false,
  },
  relative_date: {
    key: 'relative_date',
    label: 'relative range',
    description: 'a rolling or calendar-based date range',
    requiresValue: true,
    requiresRange: false,
  },
};

/** Field type categories for operator assignment */
type FieldCategory = 'text' | 'keyword' | 'number' | 'date' | 'boolean' | 'select';

/** Operators available for each field category */
const OPERATORS_BY_CATEGORY: Record<FieldCategory, FilterOperator[]> = {
  text: ['eq', 'ne', 'in', 'not_in', 'contains', 'starts_with', 'is_empty', 'is_not_empty'],
  keyword: ['eq', 'ne', 'in', 'not_in', 'contains', 'starts_with', 'is_empty', 'is_not_empty'],
  select: ['eq', 'ne', 'in', 'not_in', 'is_empty', 'is_not_empty'],
  number: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
  date: [
    'eq',
    'ne',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    'relative_date',
    'is_empty',
    'is_not_empty',
  ],
  boolean: ['eq', 'is_empty', 'is_not_empty'],
};

/** Selects a category from controlType first, then reportDatatype or fieldType; unrecognized metadata falls back to text. */
export function getFieldCategory(
  controlType: string,
  reportDatatype?: string,
  fieldType?: string
): FieldCategory {
  if (controlType === 'boolean') return 'boolean';
  if (controlType === 'date') return 'date';
  if (controlType === 'number-range') return 'number';
  if (controlType === 'select') return 'select';
  if (controlType === 'multi-select') return 'select';

  const type = reportDatatype || fieldType || '';
  if (['integer', 'long', 'float', 'double', 'short'].includes(type)) return 'number';
  if (type === 'date') return 'date';
  if (type === 'boolean') return 'boolean';

  return 'text';
}

/** Returns operator labels and input requirements for the category, falling back to text operators for an unknown category. */
export function getOperatorsForCategory(category: FieldCategory): OperatorOption[] {
  const keys = OPERATORS_BY_CATEGORY[category] || OPERATORS_BY_CATEGORY.text;
  return keys.map((k) => ALL_OPERATORS[k]);
}

/** Defaults number/date filters to between and all other categories to equality. */
export function getDefaultOperator(category: FieldCategory): FilterOperator {
  if (category === 'number' || category === 'date') return 'between';
  return 'eq';
}

/** Returns the shared label and value/range requirements for a supported operator key. */
export function getOperatorInfo(op: FilterOperator): OperatorOption {
  return ALL_OPERATORS[op];
}

/**
 * Parse a filter key into its base field key and operator.
 * e.g., "amount__gte" → { baseKey: "amount", operator: "gte" }
 *       "name" → { baseKey: "name", operator: "eq" }
 */
/**
 * Normalize an in/not_in filter value to an array of strings.
 * Handles both array values (from dropdown multi-select) and
 * comma-separated strings (from text input).
 */
export function normalizeMultiFilterValue(value: unknown): string[] {
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) return value.map(String);
  return String(value)
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

export function parseFilterKey(key: string): { baseKey: string; operator: FilterOperator } {
  const suffixes: FilterOperator[] = [
    'relative_date',
    'is_not_empty',
    'is_empty',
    'starts_with',
    'contains',
    'not_in',
    'in',
    'between',
    'gte',
    'lte',
    'gt',
    'lt',
    'ne',
  ];

  for (const suffix of suffixes) {
    const pattern = `__${suffix}`;
    if (key.endsWith(pattern)) {
      return { baseKey: key.slice(0, -pattern.length), operator: suffix };
    }
  }

  return { baseKey: key, operator: 'eq' };
}

/**
 * Filter layout — supports one level of OR groups for build filters.
 * Top-level slots are AND'd together; a group's children are OR'd.
 */
export type FilterSlot =
  { type: 'filter'; fieldKey: string } | { type: 'group'; id: string; fieldKeys: string[] };

export type FilterLayout = FilterSlot[];

/** Flatten layout to a list of field keys in display order */
export function flattenLayout(layout: FilterLayout): string[] {
  const keys: string[] = [];
  for (const slot of layout) {
    if (slot.type === 'filter') keys.push(slot.fieldKey);
    else keys.push(...slot.fieldKeys);
  }
  return keys;
}

/** Derive a flat layout from a list of field keys (migration path) */
export function layoutFromFlatKeys(fieldKeys: string[]): FilterLayout {
  return fieldKeys.map((fieldKey) => ({ type: 'filter', fieldKey }));
}

/** Generate a stable group id */
function newGroupId(): string {
  return `group-${Math.random().toString(36).slice(2, 10)}`;
}

/** Append a new empty group to the layout */
export function appendGroup(layout: FilterLayout): FilterLayout {
  return [...layout, { type: 'group', id: newGroupId(), fieldKeys: [] }];
}

/** Add a field as a top-level filter slot */
export function appendFilter(layout: FilterLayout, fieldKey: string): FilterLayout {
  if (flattenLayout(layout).includes(fieldKey)) return layout;
  return [...layout, { type: 'filter', fieldKey }];
}

/** Remove a field from the layout (top-level or inside any group) */
export function removeFieldFromLayout(layout: FilterLayout, fieldKey: string): FilterLayout {
  return layout
    .map((slot) => {
      if (slot.type === 'filter') {
        return slot.fieldKey === fieldKey ? null : slot;
      }
      const filtered = slot.fieldKeys.filter((k) => k !== fieldKey);
      return { ...slot, fieldKeys: filtered };
    })
    .filter((s): s is FilterSlot => s !== null);
}

/** Move a field into a group (removes from wherever it was) */
export function moveFieldToGroup(
  layout: FilterLayout,
  fieldKey: string,
  groupId: string
): FilterLayout {
  // Validate the target group exists before mutating; otherwise we'd remove
  // the field from its current slot and silently drop it.
  const targetExists = layout.some((slot) => slot.type === 'group' && slot.id === groupId);
  if (!targetExists) return layout;

  const without = removeFieldFromLayout(layout, fieldKey);
  return without.map((slot) => {
    if (slot.type === 'group' && slot.id === groupId) {
      return { ...slot, fieldKeys: [...slot.fieldKeys, fieldKey] };
    }
    return slot;
  });
}

/** Move a field out of any group back to top level */
export function moveFieldOutOfGroup(layout: FilterLayout, fieldKey: string): FilterLayout {
  const without = removeFieldFromLayout(layout, fieldKey);
  return [...without, { type: 'filter', fieldKey }];
}

/**
 * Dissolve a group — promote its children to top-level filter slots in place.
 * Preserves order: group children appear where the group was.
 */
export function dissolveGroup(layout: FilterLayout, groupId: string): FilterLayout {
  const out: FilterLayout = [];
  for (const slot of layout) {
    if (slot.type === 'group' && slot.id === groupId) {
      for (const fieldKey of slot.fieldKeys) {
        out.push({ type: 'filter', fieldKey });
      }
    } else {
      out.push(slot);
    }
  }
  return out;
}

/** Find the group id containing a field, or null if top-level / absent */
export function findGroupForField(layout: FilterLayout, fieldKey: string): string | null {
  for (const slot of layout) {
    if (slot.type === 'group' && slot.fieldKeys.includes(fieldKey)) return slot.id;
  }
  return null;
}
